import React from "react";

interface MousePosition {
  x: number;
  y: number;
  isInCanvas: boolean;
}

class Particle {
  canvas: HTMLCanvasElement;
  mouseRef: React.RefObject<MousePosition>;
  x: number;
  y: number;
  size: number;
  baseSpeedX: number;
  baseSpeedY: number;
  speedX: number;
  speedY: number;
  shape: number;

  constructor(
    canvas: HTMLCanvasElement,
    mouseRef: React.RefObject<MousePosition>,
  ) {
    this.canvas = canvas;
    this.mouseRef = mouseRef;
    this.x = Math.random() * canvas.width;
    this.y = Math.random() * canvas.height;
    this.size = Math.random() * 5 + 1;
    const baseSpeed = (canvas.width + canvas.height) * 0.00025;
    this.baseSpeedX = (Math.random() * 2 - 1) * baseSpeed;
    this.baseSpeedY = (Math.random() * 2 - 1) * baseSpeed;
    this.speedX = this.baseSpeedX;
    this.speedY = this.baseSpeedY;
    this.shape = Math.floor(Math.random() * 2);
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;

    if (this.x > this.canvas.width) this.x = 0;
    if (this.x < 0) this.x = this.canvas.width;
    if (this.y > this.canvas.height) this.y = 0;
    if (this.y < 0) this.y = this.canvas.height;

    this.followMouse();
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.beginPath();
    ctx.fillStyle = "rgba(107, 105, 187, 0.3)";

    switch (this.shape) {
      case 0:
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        break;
      case 1:
        ctx.rect(
          this.x - this.size,
          this.y - this.size,
          this.size * 2,
          this.size * 2,
        );
        break;
    }

    ctx.fill();
    ctx.closePath();
  }

  followMouse() {
    if (!this.mouseRef.current.isInCanvas) {
      // Return to base movement when mouse is outside
      this.speedX += (this.baseSpeedX - this.speedX) * 0.1;
      this.speedY += (this.baseSpeedY - this.speedY) * 0.1;
      return;
    }

    const dx = this.mouseRef.current.x - this.x;
    const dy = this.mouseRef.current.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const maxDistance = Math.min(this.canvas.width, this.canvas.height) * 0.1;

    if (distance < maxDistance) {
      // Smooth sigmoid-like falloff
      let strength = Math.cos((distance / maxDistance) * Math.PI) * 0.5 + 0.5;
      strength = strength ** 2; // Make the falloff more pronounced

      // Calculate target speed
      const moveSpeed =
        Math.min(this.canvas.width, this.canvas.height) * 0.00003;
      const moveX = dx * moveSpeed * strength;
      const moveY = dy * moveSpeed * strength;

      // Smooth acceleration
      this.speedX += (moveX - this.speedX) * 0.1;
      this.speedY += (moveY - this.speedY) * 0.1;

      // Add a bit of the base movement
      this.speedX += this.baseSpeedX * (1 - strength) * 0.1;
      this.speedY += this.baseSpeedY * (1 - strength) * 0.1;
    } else {
      // Return to base movement when out of range
      this.speedX += (this.baseSpeedX - this.speedX) * 0.1;
      this.speedY += (this.baseSpeedY - this.speedY) * 0.1;
    }
  }
}

const GeometricBackground = () => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const mouseRef = React.useRef<MousePosition>({
    x: 0,
    y: 0,
    isInCanvas: false,
  });
  const particlesRef = React.useRef<Particle[]>([]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let lastTime = performance.now();
    let isVisible = true;
    let resizeTimeout: NodeJS.Timeout | undefined;
    const targetFPS = 30;
    const frameDelay = 1000 / targetFPS;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Initialize
    setCanvasSize();
    const particleCount = Math.floor(
      (window.innerHeight * window.innerWidth) ** 0.96 / 20000,
    );
    particlesRef.current = Array(particleCount)
      .fill(null)
      .map(() => new Particle(canvas, mouseRef));

    // Event listeners
    window.addEventListener("resize", setCanvasSize);
    const updateMousePosition = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY,
        isInCanvas: true,
      };
    };
    const handleMouseLeave = () => {
      mouseRef.current.isInCanvas = false;
    };

    canvas.addEventListener("mousemove", updateMousePosition);
    canvas.addEventListener("mouseleave", handleMouseLeave);

    // Visibility detection - pause when tab is hidden
    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
      if (isVisible) {
        lastTime = performance.now();
        animationFrameId = requestAnimationFrame(render);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Render loop
    const render = (currentTime: number) => {
      if (!isVisible) return;

      const deltaTime = currentTime - lastTime;

      // Throttle to target FPS
      if (deltaTime < frameDelay) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      lastTime = currentTime - (deltaTime % frameDelay);

      // Update particles
      particlesRef.current.forEach((particle) => {
        particle.update();
      });

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw particles
      particlesRef.current.forEach((particle) => {
        particle.draw(ctx);
      });

      // Draw connections directly
      const particles = particlesRef.current;
      const connectionDistance = 120;
      const connectionDistanceSq = connectionDistance * connectionDistance;
      ctx.lineWidth = 1;

      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];

        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distanceSq = dx * dx + dy * dy;

          if (distanceSq < connectionDistanceSq) {
            const distance = Math.sqrt(distanceSq);
            const alpha = 0.25 - distance / 400;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(107, 105, 187, ${alpha})`;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            ctx.closePath();
          }
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render(performance.now());

    return () => {
      window.removeEventListener("resize", setCanvasSize);
      canvas.removeEventListener("mousemove", updateMousePosition);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 bottom-0 right-0 z-decorative"
    />
  );
};

export default GeometricBackground;
